import React, {
  CSSProperties,
  forwardRef,
  ForwardRefRenderFunction,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Icon } from '../icons/icon/icon-component';
import { Responsive } from '../responsive/responsive-component';
import isMobileDevice from '../responsive/responsive.util';
import CustomScrollbar, { CustomScrollbarRef } from '../scrollbar/scrollbar-component';
import styles from './modal-component.module.scss';

export interface ModalRef {
  closeModal: (e: React.SyntheticEvent) => void;
}

type ModalProps = {
  headerElt?: ReactNode;
  footerElt?: ReactNode;
  mobileFooterElt?: ReactNode;
  hideCloseIcon?: boolean;
  isClosable?: boolean;
  closeIconTabIndex?: number;
  isMobileFullScreen?: boolean;
  isMobileCentred?: boolean;
  /**Applicable to both Desktop & Tablet. `isSideBarTablet` overrides this prop if set for Tablet only. */
  isSidebar?: boolean;
  isSidebarTablet?: boolean;
  headerClassName?: string;
  footerClassName?: string;
  className?: string;
  modalContainerClassName?: string;
  isWhiteBackgroundColor?: boolean;
  hideOverlay?: boolean;
  showFooterGteTablet?: boolean;
  mobileNoScroll?: boolean;
  /**Prioritise the click of the close icon (using mouseDown event) */
  prioritiseCloseIconClick?: boolean;
  showSidebarHeaderBottomBorder?: boolean;
  showMobileNotCentredHeaderBottomBorder?: boolean;
  closeCallback?: (e: React.SyntheticEvent | PopStateEvent) => void;
  scrolledToBottom?: (container: HTMLElement) => void;
};

/**
 * Modal component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=97%3A1989
 */
const ModalForwardRef: ForwardRefRenderFunction<ModalRef, PropsWithChildren<ModalProps>> = (
  {
    headerElt,
    footerElt = null,
    mobileFooterElt = null,
    isClosable = true,
    hideCloseIcon = false,
    closeIconTabIndex = 0,
    isMobileFullScreen = false,
    isSidebar = false,
    className = '',
    hideOverlay = false,
    prioritiseCloseIconClick = false,
    showSidebarHeaderBottomBorder = true,
    showMobileNotCentredHeaderBottomBorder = false,
    isSidebarTablet = undefined,
    isMobileCentred = false,
    modalContainerClassName = undefined,
    isWhiteBackgroundColor = false,
    mobileNoScroll = false,
    showFooterGteTablet = false,
    headerClassName = undefined,
    footerClassName = undefined,
    children,
    scrolledToBottom,
    closeCallback,
  },
  ref
) => {
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrollUpdated, setIsScrollUpdated] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false); //used for mobile scrollable slideup to increase size to full screen upon strating to scroll
  const [isMobileSlideUpScrolled, setIsMobileSlideUpScrolled] = useState(isScrolling && !isMobileFullScreen);
  const [innerHeight, setInnerHeight] = useState(0);
  const [lastY, setLastY] = useState(0);

  const node = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<CustomScrollbarRef>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const currentHash = window.location.hash;

  useLayoutEffect(() => {
    document.body.classList.add('no-scroll');

    return () => {
      // Cleanup & unsubscribe
      document.body.classList.remove('no-scroll');
    };
  }, []);

  useLayoutEffect(() => {
    // set innerHeight
    setInnerHeight(window.innerHeight);

    // call update scroll to re-calculate scroll length
    scrollbarRef.current?.updateScroll();
    setIsScrollUpdated(true);
  }, []);

  //detect initial touch position to determine direction of vertical scroll on mobile
  useLayoutEffect(() => {
    function onTouchStart(e: TouchEvent) {
      if (!isMobileSlideUpScrolled) {
        setLastY(e.touches[0].pageY);
      }
    }

    if (!isMobileFullScreen && isMobileDevice()) {
      window.addEventListener('touchstart', onTouchStart);

      if (isMobileSlideUpScrolled) window.removeEventListener('touchstart', onTouchStart);

      return () => {
        window.removeEventListener('touchstart', onTouchStart);
      };
    }
  }, [isMobileSlideUpScrolled, isMobileFullScreen]);

  //detect vertical touch scroll on mobile to open slideup modal to fullscreen even when contents are not scrollable
  useLayoutEffect(() => {
    function onTouchMove(e: TouchEvent) {
      const currentY = e.touches[0].pageY;

      if (!isMobileSlideUpScrolled && currentY <= lastY) {
        setIsScrolling(true);
        setIsMobileSlideUpScrolled(true);
      }
    }

    if (!isMobileFullScreen && isMobileDevice()) {
      window.addEventListener('touchmove', onTouchMove);

      if (isMobileSlideUpScrolled) window.removeEventListener('touchmove', onTouchMove);

      return () => {
        window.removeEventListener('touchmove', onTouchMove);
      };
    }
  }, [isMobileSlideUpScrolled, lastY, isMobileFullScreen]);

  useEffect(() => {
    if (isMobileDevice()) {
      if (window.location.hash.startsWith('#')) {
        window.location.hash += '_';
      } else {
        window.location.hash = '#_';
      }
    }
  }, []);

  useEffect(() => {
    function pop2(e: PopStateEvent) {
      //if pop state event called due to back button click
      if (currentHash === window.location.hash) {
        if (isMobileDevice()) closeModalDisplay(e);
      }
    }

    window.addEventListener('popstate', pop2);
    return () => {
      window.removeEventListener('popstate', pop2);
    };
    // this needs to run only once, otherwise it causes issues, so set deps to empty array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if isClosable adds event listeners to close modal and removes them once
  // modal is destroyed
  useEffect(() => {
    function onResize() {
      // re-calculate inner height
      setInnerHeight(window.innerHeight);

      // call update scroll to re-calculate scroll length
      scrollbarRef.current?.updateScroll();

      //to re-calculate footer height
      document.documentElement.style.setProperty('--footer-height', footerRef.current?.clientHeight + 'rem');

      //to re-calculate header height
      if (headerElt !== null) {
        document.documentElement.style.setProperty('--header-height', headerRef.current?.clientHeight + 'rem');
      }
    }

    if (isClosable) {
      window.addEventListener('resize', onResize);
    }
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [headerElt, isClosable]);

  useEffect(() => {
    //to remove backdrop and stop displaying component
    if (isCollapsed) {
      collapseTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 500);

      return () => {
        if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
      };
    }
  }, [isCollapsed]);

  //to calculate initial header height
  useEffect(() => {
    if (headerElt !== null) {
      document.documentElement.style.setProperty('--header-height', headerRef.current?.clientHeight + 'rem');
    }
  }, [headerElt, headerRef]);

  //to calculate initial footer height
  useEffect(() => {
    document.documentElement.style.setProperty('--footer-height', footerRef.current?.clientHeight + 'rem');
  }, [footerRef]);

  // expose internal methods
  useImperativeHandle(ref, () => ({
    closeModal: closeModal,
  }));

  const footer = footerElt || mobileFooterElt;

  return (
    <div
      className={`${styles['modal-component']} ${modalContainerClassName ?? ''}`}
      style={{ display: isOpen ? 'block' : 'none', '--full-height': `${innerHeight}px` } as CSSProperties}
    >
      <div
        data-testid='modal'
        ref={node}
        className={`${styles.modal} ${isSidebar ? styles.sidebar : styles.normal} ${
          isSidebarTablet !== undefined ? (isSidebarTablet ? styles['sidebar-tablet'] : styles['normal-tablet']) : ''
        } ${
          mobileNoScroll
            ? styles['no-scroll']
            : isMobileFullScreen
            ? styles.fullscreen
            : isMobileCentred
            ? styles.centred
            : `${isMobileSlideUpScrolled ? styles.full : styles.small} ${
                isCollapsed
                  ? isMobileSlideUpScrolled
                    ? styles['full-slideup']
                    : styles.slideup
                  : isScrolling
                  ? styles['full-slidedown']
                  : styles.slidedown
              } `
        } ${className}`}
      >
        {/* Only show if we have a header or close icon */}
        {(headerElt || !hideCloseIcon) && (
          <div
            ref={headerRef}
            className={`${styles.header} ${headerClassName ? headerClassName : ''} ${
              isSidebar && showSidebarHeaderBottomBorder ? styles['header-border'] : ''
            } ${showMobileNotCentredHeaderBottomBorder ? styles['mobile-header-border'] : ''}`}
          >
            <div className={styles['header-contents']}>{headerElt}</div>
            {isClosable && !hideCloseIcon && (
              <div
                className={styles['close-icon']}
                tabIndex={closeIconTabIndex ?? 0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') closeModal(e);
                }}
                data-testid='modal-close'
              >
                <Icon
                  data-testid='modal-close-icon'
                  name='close'
                  onMouseDown={(e) => prioritiseCloseIconClick && closeModal(e)}
                  onClick={(e) => !prioritiseCloseIconClick && closeModal(e)}
                />
              </div>
            )}
          </div>
        )}
        <CustomScrollbar
          data-testid='modal-scrollbar'
          className={`${footer ? styles['scroll-container-footer'] : styles['scroll-container-no-footer']} ${
            isWhiteBackgroundColor ? styles['white-background'] : ''
          }`}
          showOnLoad={true}
          onScrollY={() => {
            setIsScrolling(true);
            setIsMobileSlideUpScrolled(!isMobileFullScreen);
          }}
          onYReachEnd={(e) => {
            if (isScrollUpdated) {
              scrolledToBottom?.(e);
            }
          }}
          ref={scrollbarRef}
        >
          {children}
        </CustomScrollbar>
        <div
          ref={footerRef}
          className={`${footer ? styles.footer : styles['no-footer']} ${
            isSidebar || isSidebarTablet ? (footerElt ? styles['sidebar-footer'] : '') : ''
          } ${footerClassName ?? ''} ${showFooterGteTablet ? styles['show-footer-gte-tablet'] : ''}`}
        >
          <div className={styles['footer-contents']}>
            <Responsive breakpoints={['mobile']}>{mobileFooterElt ?? footerElt}</Responsive>
            <Responsive breakpoints={['tablet', 'desktop', 'lg-desktop']}>{footerElt}</Responsive>
          </div>
        </div>
      </div>
      <div
        className={`${styles.overlay} ${hideOverlay ? styles['hide-overlay'] : ''}`}
        onClick={closeModal}
        data-testid='modal-overlay'
      ></div>
    </div>
  );

  function closeModalDisplay(e: React.SyntheticEvent | PopStateEvent) {
    if (isMobileDevice() && !isMobileFullScreen) {
      setIsCollapsed(true);
    } else {
      setIsOpen(false);
    }

    closeCallback?.(e);
  }

  function closeModal(e: React.SyntheticEvent) {
    if (isClosable) {
      closeModalDisplay(e);
      setIsScrolling(false);

      //if mobile modal closed normally (close icon or clicking outside)
      if (isMobileDevice() && window.location.hash.includes('_')) {
        if (window.location.hash === '#_') {
          //remove last hash at end of URL
          window.history.pushState('', document.title, window.location.pathname + window.location.search);
        } else {
          //remove 1 hash at end of URL
          const tempHash = window.location.hash.replace('_', '');

          window.history.pushState('', document.title, window.location.pathname + window.location.search + tempHash);
        }
      }
    }
  }
};

export const Modal = forwardRef(ModalForwardRef);

export default Modal;
